import React, { useState } from 'react';
import { useSyncPrograms } from '../../hooks/useSync';
import { SyncCredentials } from '../../services/api';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const SyncForm: React.FC = () => {
  const [credentials, setCredentials] = useState<SyncCredentials>({
    publisherId: '',
    accessToken: '',
    countryCode: 'DE',
    includeHidden: false,
    relationship: 'joined',
  });

  const syncMutation = useSyncPrograms();

  const handleSync = async () => {
    if (!credentials.publisherId || !credentials.accessToken) {
      return;
    }

    try {
      await syncMutation.mutateAsync(credentials);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 lg:p-6 border border-gray-200 dark:border-gray-700">
      <h2 className="text-lg lg:text-xl font-semibold mb-4 lg:mb-6 text-gray-900 dark:text-white">AWIN Sync Configuration</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Publisher ID
          </label>
          <input
            type="text"
            name="publisherId"
            value={credentials.publisherId}
            onChange={handleInputChange}
            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 p-2 lg:p-3"
            placeholder="Enter your AWIN Publisher ID"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Access Token
          </label>
          <input
            type="password"
            name="accessToken"
            value={credentials.accessToken}
            onChange={handleInputChange}
            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 p-2 lg:p-3"
            placeholder="Enter your AWIN Access Token"
          />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Country Code
            </label>
            <select
              name="countryCode"
              value={credentials.countryCode}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 lg:p-3"
            >
              <option value="DE">Germany (DE)</option>
              <option value="AT">Austria (AT)</option>
              <option value="CH">Switzerland (CH)</option>
              <option value="NL">Netherlands (NL)</option>
              <option value="BE">Belgium (BE)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Relationship
            </label>
            <select
              name="relationship"
              value={credentials.relationship}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 lg:p-3"
            >
              <option value="joined">Joined</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="includeHidden"
            name="includeHidden"
            checked={credentials.includeHidden}
            onChange={handleInputChange}
            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-700"
          />
          <label htmlFor="includeHidden" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Include hidden programs
          </label>
        </div>
        
        <button
          onClick={handleSync}
          disabled={syncMutation.isPending || !credentials.publisherId || !credentials.accessToken}
          className="w-full py-3 px-4 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
        >
          {syncMutation.isPending ? (
            <>
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              <span>Syncing...</span>
            </>
          ) : (
            <>
              <ArrowPathIcon className="h-5 w-5" />
              <span>Sync Programs</span>
            </>
          )}
        </button>
      </div>
      
      {syncMutation.data && (
        <div className="mt-6 p-4 bg-success-50 dark:bg-success-900/20 rounded-md border border-success-200 dark:border-success-800">
          <h3 className="font-semibold text-success-800 dark:text-success-200 mb-2">Sync Results</h3>
          <div className="space-y-1 text-sm text-success-700 dark:text-success-300">
            <div>✅ Created: {syncMutation.data.created} programs</div>
            <div>✅ Updated: {syncMutation.data.updated} programs</div>
            <div>✅ Total: {syncMutation.data.total} programs</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncForm; 